<template>
  <paginated-table
    id="admin-loanables-table"
    ref="table"
    endpoint="loanables"
    :fetch-params="{
      for: 'admin',
    }"
    :label="$t('views.admin.loanables.title')"
    :columns="columns"
    :load-relations="['active_incidents', 'merged_user_roles.user', 'current_loan.borrower_user']"
    :extra-filters="extraFilters"
    :show-generate-csv="false"
    sync-filters-with-url
    row-link
  >
    <template #head-buttons>
      <icon-button v-if="canCreateLoanable" to="/admin/loanables/new" role="add">
        {{ $t("views.admin.loanables.create") | capitalize }}
      </icon-button>
    </template>

    <template #cell(availability_status)="{ item }">
      <availability-status :loanable="item"
    /></template>

    <template #cell(actions)="{ item }">
      <loanable-list-actions :loanable="item" @changed="$refs.table.refresh()" />
    </template>
  </paginated-table>
</template>

<script>
import LoanableListActions from "@/components/Loanable/LoanableListActions.vue";
import AvailabilityStatus from "@/components/Loanable/AvailabilityStatus.vue";
import IconButton from "@/components/shared/IconButton.vue";
import PaginatedTable from "@/components/shared/PaginatedTable.vue";
import { Column, Filter } from "@/components/shared/PaginatedTableColumns";
import { loanBorrowerLink, userLink } from "@/helpers/links";
import { capitalize } from "@/helpers/filters";
import { getCommunityAdminFilter } from "@/helpers/permissions/communities";
import { canCreateLoanable } from "@/helpers/permissions/loanables";

export default {
  name: "AdminLoanables",
  components: {
    AvailabilityStatus,
    IconButton,
    PaginatedTable,
    LoanableListActions,
  },
  computed: {
    extraFilters() {
      return [
        new Filter(
          "sharedInCommunity",
          capitalize(this.$t("communities.fields.community.name")),
          "relation",
          {
            relation: "communities",
            label: "name",
            field: "id",
            params: { ...getCommunityAdminFilter(this.$store.state.user, "read", "id") },
          }
        ),
        new Filter(
          "is_deleted",
          capitalize(this.$t("views.admin.loanables.filters.archived")),
          "boolean"
        ),
      ];
    },
    columns() {
      return [
        new Column("id", capitalize(this.$t("lists.id")), "id"),
        new Column("name", capitalize(this.$t("views.admin.loanables.columns.name")), "text"),
        Column.withSelect("type", capitalize(this.$t("views.admin.loanables.columns.type")), [
          { value: null, label: capitalize(this.$t("loanables.fields.types.all")) },
          { value: "car", label: capitalize(this.$t("loanables.fields.types.car")) },
          { value: "trailer", label: capitalize(this.$t("loanables.fields.types.trailer")) },
          { value: "bike", label: capitalize(this.$t("loanables.fields.types.bike")) },
          {
            value: "car_trailer",
            label: capitalize(this.$t("loanables.fields.types.car_trailer")),
          },
        ]),
        Column.withRelation(
          "owner_user.full_name",
          capitalize(this.$t("views.admin.loanables.columns.owner")),
          {
            relation: "users",
            label: "full_name",
            field: "id",
            params: { ...getCommunityAdminFilter(this.$store.state.user, "read") },
          },
          "owner_user_id",
          {
            urlFct: (loanable) => userLink(loanable.owner_user),
            sortable: false,
          }
        ),
        new Column(
          "current_loan.id",
          capitalize(this.$t("views.admin.loanables.columns.current_loan")),
          "text",
          {
            urlFct: (loanable) => `/admin/loans/${loanable.current_loan?.id}`,
            sortable: false,
          }
        ),
        new Column(
          "current_loan.borrower_user.id",
          capitalize(this.$t("views.admin.loanables.columns.borrower_id")),
          "id",
          {
            showByDefault: false,
          }
        ),
        new Column(
          "current_loan.borrower_user.full_name",
          capitalize(this.$t("views.admin.loanables.columns.borrower")),
          "text",
          {
            urlFct: (loanable) => loanBorrowerLink(loanable.current_loan),
            sortable: false,
          }
        ),
        Column.withSelect(
          "availability_status",
          capitalize(this.$t("views.admin.loanables.columns.availability_status")),
          [
            { value: null, label: this.$t("views.admin.loanables.availability_status.all") },
            {
              value: "no_approved_communities",
              label: this.$t("views.admin.loanables.availability_status.no_approved_communities"),
              variant: "warning",
            },
            {
              value: "wrong_type",
              label: this.$t("views.admin.loanables.availability_status.wrong_type"),
              variant: "danger",
            },
            {
              value: "no_availabilities",
              label: this.$t("views.admin.loanables.availability_status.no_availabilities"),
              variant: "warning",
            },
            {
              value: "unpublished",
              label: this.$t("views.admin.loanables.availability_status.unpublished"),
              variant: "secondary",
            },
            {
              value: "has_availabilities",
              label: this.$t("views.admin.loanables.availability_status.has_availabilities"),
              variant: "success",
            },
          ],
          { sortable: false }
        ),
        new Column(
          "deleted_at",
          capitalize(this.$t("views.admin.loanables.columns.deletion_date")),
          "date",
          {
            showByDefault: false,
          }
        ),
        Column.withSelect(
          "sharing_mode",
          capitalize(this.$t("loanables.fields.sharing_mode.title")),
          [
            {
              value: null,
              label: capitalize(this.$t("loanables.fields.sharing_mode.options.null.all")),
            },
            {
              value: "on_demand",
              label: capitalize(this.$t("loanables.fields.sharing_mode.options.on_demand")),
            },
            {
              value: "self_service",
              label: capitalize(this.$t("loanables.fields.sharing_mode.options.self_service")),
            },
            {
              value: "hybrid",
              label: capitalize(this.$t("loanables.fields.sharing_mode.options.hybrid")),
            },
          ],
          { showByDefault: false }
        ),
      ];
    },
    loggedInUser() {
      return this.$store.state.user;
    },
    canCreateLoanable() {
      return canCreateLoanable(this.loggedInUser);
    },
  },
};
</script>

<style lang="scss"></style>
